// MeMeet kullanıcı tercihleri (2026-10-14) — klavye kısayolları, SoundPad sesi, katılımcı ses ayarları.
// Hesaba bağlı saklanır (flow/memeet-get-preferences | memeet-save-preferences) → masaüstü, web panel,
// standalone ve mobil MeMeet'te aynı. localStorage önbelleği ilk açılışı anında yapar; sunucu cevabı gelince güncellenir.
//
// Kısayol biçimi platformdan bağımsız: "Mod+Shift+KeyD"
//   Mod  = Mac'te ⌘ (Cmd), Windows/Linux'ta Ctrl
//   Ctrl = Mac'te ⌃ (Control) — yalnız Mac'te Mod'dan ayrı anlam taşır
//   Meta = Windows'ta Win, Linux'ta Super (Mac'te Mod zaten ⌘)
//   Tuş  = KeyboardEvent.code (KeyD, Digit1, F5, Space…) — klavye dili (TR-Q/F, DE…) değişse de aynı fiziksel tuş.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type ShortcutAction =
  | "toggleMic" | "toggleCamera" | "toggleSpeaker" | "pushToTalk" | "toggleScreenShare"
  | "toggleHand" | "toggleChat" | "toggleSoundpad" | "openSettings" | "leaveCall";

export const SHORTCUT_ACTIONS: ShortcutAction[] = [
  "toggleMic", "toggleCamera", "toggleSpeaker", "pushToTalk", "toggleScreenShare",
  "toggleHand", "toggleChat", "toggleSoundpad", "openSettings", "leaveCall",
];

/** Varsayılanlar: tarayıcı/işletim sistemi kısayollarıyla (⌘W/⌘T/⌘Q, Ctrl+Space başlatıcı) çakışmayan seçim.
 *  Bas-konuş ve görüşmeden çık bilerek boş: yanlışlıkla tetiklenmesin. */
export const DEFAULT_SHORTCUTS: Record<ShortcutAction, string> = {
  toggleMic: "Mod+KeyD",
  toggleCamera: "Mod+KeyE",
  toggleSpeaker: "Mod+Shift+KeyD",
  pushToTalk: "",
  toggleScreenShare: "Mod+Shift+KeyE",
  toggleHand: "Mod+Shift+KeyH",
  toggleChat: "Mod+Shift+KeyU",
  toggleSoundpad: "Mod+Shift+KeyS",
  openSettings: "Mod+Comma",
  leaveCall: "",
};

/** Masaüstünde uygulama arka plandayken de çalışabilen eylemler (Electron globalShortcut).
 *  Bas-konuş YOK: globalShortcut tuşun bırakılmasını bildirmez. */
export const GLOBAL_CAPABLE: ShortcutAction[] = ["toggleMic", "toggleSpeaker"];

export type ParticipantVolume = { v: number; m: 0 | 1 };
export type MeMeetPrefs = {
  shortcuts: Record<string, string>;
  globalShortcuts: string[];
  soundpadVolume: number;
  participantVolumes: Record<string, ParticipantVolume>;
};
const EMPTY: MeMeetPrefs = { shortcuts: {}, globalShortcuts: [], soundpadVolume: 0.8, participantVolumes: {} };
const CACHE_KEY = "memeet:prefs:v1";

const KEY_RE = /^((Mod|Ctrl|Alt|Shift|Meta)\+){0,4}(Key[A-Z]|Digit[0-9]|F([1-9]|1[0-9]|2[0-4])|Space|Backquote|Minus|Equal|BracketLeft|BracketRight|Backslash|Semicolon|Quote|Comma|Period|Slash|Arrow(Up|Down|Left|Right)|Numpad[0-9]|NumpadAdd|NumpadSubtract|NumpadMultiply|NumpadDivide|Insert|Home|End|PageUp|PageDown|Pause|ScrollLock)$/;

// ---------------------------------------------------------------------------
// Platform
// ---------------------------------------------------------------------------
export type Platform = "mac" | "windows" | "linux";
export function detectPlatform(): Platform {
  if (typeof navigator === "undefined") return "windows";
  const nav = navigator as any;
  const p = String(nav.userAgentData?.platform || nav.platform || nav.userAgent || "").toLowerCase();
  if (/mac|iphone|ipad|ipod/.test(p)) return "mac";
  if (/linux|x11|cros/.test(p) && !/android/.test(p)) return "linux";
  return "windows";
}

// ---------------------------------------------------------------------------
// Kısayol dönüşümleri
// ---------------------------------------------------------------------------
const MODIFIER_CODES = new Set(["ShiftLeft", "ShiftRight", "ControlLeft", "ControlRight", "AltLeft", "AltRight", "MetaLeft", "MetaRight", "OSLeft", "OSRight", "CapsLock", "Fn", "FnLock"]);

/** Klavye olayından kısayol dizesi (yalnız değiştirici tuşa basıldıysa null). */
export function comboFromEvent(e: KeyboardEvent | { code: string; metaKey: boolean; ctrlKey: boolean; altKey: boolean; shiftKey: boolean }, platform: Platform): string | null {
  const code = String(e.code || "");
  if (!code || MODIFIER_CODES.has(code)) return null;
  const parts: string[] = [];
  if (platform === "mac") {
    if (e.metaKey) parts.push("Mod");
    if (e.ctrlKey) parts.push("Ctrl");
  } else {
    if (e.ctrlKey) parts.push("Mod");
    if (e.metaKey) parts.push("Meta");
  }
  if (e.altKey) parts.push("Alt");
  if (e.shiftKey) parts.push("Shift");
  const combo = [...parts, code].join("+");
  return KEY_RE.test(combo) ? combo : null;
}

const KEY_LABELS: Record<string, string> = {
  Space: "Space", Backquote: "`", Minus: "-", Equal: "=", BracketLeft: "[", BracketRight: "]", Backslash: "\\",
  Semicolon: ";", Quote: "'", Comma: ",", Period: ".", Slash: "/", ArrowUp: "↑", ArrowDown: "↓", ArrowLeft: "←", ArrowRight: "→",
  NumpadAdd: "Num +", NumpadSubtract: "Num -", NumpadMultiply: "Num *", NumpadDivide: "Num /", PageUp: "PgUp", PageDown: "PgDn",
  ScrollLock: "ScrLk",
};
function keyLabel(code: string): string {
  if (KEY_LABELS[code]) return KEY_LABELS[code];
  if (code.startsWith("Key")) return code.slice(3);
  if (code.startsWith("Digit")) return code.slice(5);
  if (code.startsWith("Numpad")) return "Num " + code.slice(6);
  return code;
}

/** Ekranda gösterim: Mac "⌘⇧D", Windows "Ctrl+Shift+D", Linux "Ctrl+Shift+D" (Meta → Win/Super). */
export function formatCombo(combo: string, platform: Platform): string {
  if (!combo) return "";
  const parts = combo.split("+");
  const code = parts.pop() || "";
  if (platform === "mac") {
    const sym: Record<string, string> = { Ctrl: "⌃", Alt: "⌥", Shift: "⇧", Mod: "⌘", Meta: "⌘" };
    const order = ["Ctrl", "Alt", "Shift", "Mod", "Meta"];   // Apple sırası: ⌃⌥⇧⌘
    return order.filter((m) => parts.includes(m)).map((m) => sym[m]).join("") + keyLabel(code);
  }
  const name: Record<string, string> = { Mod: "Ctrl", Ctrl: "Ctrl", Alt: "Alt", Shift: "Shift", Meta: platform === "linux" ? "Super" : "Win" };
  const order = ["Mod", "Ctrl", "Meta", "Alt", "Shift"];
  return [...order.filter((m) => parts.includes(m)).map((m) => name[m]), keyLabel(code)].join("+");
}

/** Electron globalShortcut hızlandırıcısı ("CommandOrControl+Shift+D"); desteklenmeyen tuşta null. */
export function toElectronAccelerator(combo: string): string | null {
  if (!combo) return null;
  const parts = combo.split("+");
  const code = parts.pop() || "";
  const mods = parts.map((m) => ({ Mod: "CommandOrControl", Ctrl: "Control", Alt: "Alt", Shift: "Shift", Meta: "Super" } as Record<string, string>)[m]).filter(Boolean);
  const keyMap: Record<string, string> = {
    Space: "Space", Backquote: "`", Minus: "-", Equal: "=", BracketLeft: "[", BracketRight: "]", Backslash: "\\", Semicolon: ";", Quote: "'",
    Comma: ",", Period: ".", Slash: "/", ArrowUp: "Up", ArrowDown: "Down", ArrowLeft: "Left", ArrowRight: "Right", Insert: "Insert", Home: "Home",
    End: "End", PageUp: "PageUp", PageDown: "PageDown", NumpadAdd: "numadd", NumpadSubtract: "numsub", NumpadMultiply: "nummult", NumpadDivide: "numdiv",
    ScrollLock: "Scrolllock",
  };
  let key = keyMap[code];
  if (!key && code.startsWith("Key")) key = code.slice(3);
  if (!key && code.startsWith("Digit")) key = code.slice(5);
  if (!key && /^F\d+$/.test(code)) key = code;
  if (!key && code.startsWith("Numpad")) key = "num" + code.slice(6);
  if (!key) return null;
  return [...mods, key].join("+");
}

/** Yazı alanındayken yalnız Mod/Ctrl/Meta içeren kısayollar çalışır (Alt+harf Mac'te özel karakter, tek tuş yazıdır). */
export function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === "textarea" || tag === "select") return true;
  if (tag === "input") {
    const type = String((el as HTMLInputElement).type || "text").toLowerCase();
    return !["checkbox", "radio", "range", "button", "submit", "color", "file"].includes(type);
  }
  return !!el.isContentEditable;
}
export function comboAllowedWhileTyping(combo: string): boolean {
  return /(^|\+)(Mod|Ctrl|Meta)\+/.test(combo + "+");
}

// ---------------------------------------------------------------------------
// Depo (hook) — sunucu + yerel önbellek
// ---------------------------------------------------------------------------
function readCache(): MeMeetPrefs {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return { ...EMPTY };
    return normalize(JSON.parse(raw));
  } catch {
    return { ...EMPTY };
  }
}
function normalize(p: any): MeMeetPrefs {
  const out: MeMeetPrefs = { ...EMPTY, shortcuts: {}, globalShortcuts: [], participantVolumes: {} };
  if (p && typeof p === "object") {
    if (p.shortcuts && typeof p.shortcuts === "object") for (const [k, v] of Object.entries(p.shortcuts)) if (typeof v === "string" && (v === "" || KEY_RE.test(v))) out.shortcuts[k] = v;
    if (Array.isArray(p.globalShortcuts)) out.globalShortcuts = p.globalShortcuts.filter((a: any) => GLOBAL_CAPABLE.includes(a));
    if (typeof p.soundpadVolume === "number" && isFinite(p.soundpadVolume)) out.soundpadVolume = Math.max(0, Math.min(1, p.soundpadVolume));
    if (p.participantVolumes && typeof p.participantVolumes === "object")
      for (const [k, v] of Object.entries<any>(p.participantVolumes)) if (v && typeof v === "object") out.participantVolumes[k] = { v: Math.max(0, Math.min(1, Number(v.v ?? 1))), m: v.m ? 1 : 0 };
  }
  return out;
}

export type MeMeetPrefsApi = {
  prefs: MeMeetPrefs;
  platform: Platform;
  /** Varsayılanlarla birleşmiş geçerli kısayollar (eylem → kısayol; "" = atanmamış). */
  shortcuts: Record<string, string>;
  setShortcut: (action: string, combo: string | null) => void;   // null = varsayılana dön
  resetShortcuts: () => void;
  setGlobal: (action: ShortcutAction, on: boolean) => void;
  setSoundpadVolume: (v: number) => void;
  getParticipantVolume: (identity: string) => ParticipantVolume;
  setParticipantVolume: (identity: string, patch: Partial<ParticipantVolume>) => void;
  reload: () => void;
};

export function useMeMeetPrefs(opts: { token?: string | null; apiBase?: string }): MeMeetPrefsApi {
  const platform = useMemo(detectPlatform, []);
  const [prefs, setPrefs] = useState<MeMeetPrefs>(readCache);
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const saveTimer = useRef<number | null>(null);
  const base = (opts.apiBase || "https://prod.apiollon.com/").replace(/\/?$/, "/");
  const token = opts.token || "";

  const call = useCallback(async (name: string, body: any) => {
    if (!token) return null;
    const res = await fetch(`${base}flow/${name}`, { method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify(body || {}) });
    const d = await res.json().catch(() => null);
    return d && d.status ? d.result : null;
  }, [base, token]);

  const reload = useCallback(() => {
    void call("memeet-get-preferences", {}).then((r) => {
      if (!r || !r.prefs) return;
      const p = normalize(r.prefs);
      // Sunucuda hiç kayıt yoksa yerel önbellek korunur (ilk kullanımda ayarlar kaybolmasın)
      if (!Object.keys(r.prefs).length) return;
      setPrefs(p);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(p)); } catch { /* yok */ }
    }).catch(() => undefined);
  }, [call]);
  useEffect(() => { reload(); }, [reload]);

  const commit = useCallback((next: MeMeetPrefs) => {
    setPrefs(next);
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(next)); } catch { /* yok */ }
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => { void call("memeet-save-preferences", { prefs: prefsRef.current }).catch(() => undefined); }, 700);
  }, [call]);

  const shortcuts = useMemo(() => {
    const out: Record<string, string> = { ...DEFAULT_SHORTCUTS };
    for (const [k, v] of Object.entries(prefs.shortcuts)) out[k] = v;
    return out;
  }, [prefs.shortcuts]);

  const setShortcut = useCallback((action: string, combo: string | null) => {
    const cur = prefsRef.current;
    const sc = { ...cur.shortcuts };
    if (combo === null) delete sc[action];
    else {
      // Aynı kısayol başka eylemdeyse oradan kaldır (tek tuş → tek eylem)
      if (combo) for (const [a, c] of Object.entries({ ...DEFAULT_SHORTCUTS, ...sc })) if (a !== action && c === combo) sc[a] = "";
      sc[action] = combo;
    }
    commit({ ...cur, shortcuts: sc });
  }, [commit]);
  const resetShortcuts = useCallback(() => commit({ ...prefsRef.current, shortcuts: {}, globalShortcuts: [] }), [commit]);
  const setGlobal = useCallback((action: ShortcutAction, on: boolean) => {
    const cur = prefsRef.current;
    const set = new Set(cur.globalShortcuts);
    if (on) set.add(action); else set.delete(action);
    commit({ ...cur, globalShortcuts: Array.from(set) });
  }, [commit]);
  const setSoundpadVolume = useCallback((v: number) => commit({ ...prefsRef.current, soundpadVolume: Math.max(0, Math.min(1, v)) }), [commit]);
  const getParticipantVolume = useCallback((identity: string): ParticipantVolume => prefs.participantVolumes[identity] || { v: 1, m: 0 }, [prefs.participantVolumes]);
  const setParticipantVolume = useCallback((identity: string, patch: Partial<ParticipantVolume>) => {
    if (!identity) return;
    const cur = prefsRef.current;
    const prev = cur.participantVolumes[identity] || { v: 1, m: 0 };
    const next = { v: Math.max(0, Math.min(1, patch.v ?? prev.v)), m: (patch.m ?? prev.m) ? 1 : 0 } as ParticipantVolume;
    const pv = { ...cur.participantVolumes };
    if (next.v >= 1 && !next.m) delete pv[identity]; else pv[identity] = next;
    commit({ ...cur, participantVolumes: pv });
  }, [commit]);

  return { prefs, platform, shortcuts, setShortcut, resetShortcuts, setGlobal, setSoundpadVolume, getParticipantVolume, setParticipantVolume, reload };
}
